import React, { useEffect, useState, useRef } from "react";
import DefaultLayout from "../components/DefaultLayout";
import axios from "axios";
import { message, Input, Modal, Select, TimePicker, Row, Col, Table } from "antd";
import TransactionRecordTable from "./TransactionRecordTable";
import config from "../config";
import { getCurrencySymbol } from "../utils/currencyHelper";
import { useCurrency } from "../contexts/ValidationContext";
import Invoice from "../components/Invoice.js";
import CloseButton from "../components/CloseButton";

import moment from "moment";
const StaffTransactionRecordPage = () => {
  const apiBaseUrl = config.REACT_APP_API_BASE_URL;
  const [transactionData, setTransactionData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [restaurantName, setRestaurantName] = useState(null);
  const [imageLogo, setImageLogo] = useState(null);
  const [restaurantAddress, setRestaurantAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const { currency } = useCurrency();
  const symbol = getCurrencySymbol(currency);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const invoiceRef = useRef();
  const [isPrinting, setIsPrinting] = useState(false);
  const [filteredTransactionData, setFilteredTransactionData] = useState([]);
  const [userIdOptions, setUserIdOptions] = useState([]);
  const [tableNumberOptions, setTableNumberOptions] = useState([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedTableNumber, setSelectedTableNumber] = useState("");
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [timeRange, setTimeRange] = useState([null, null]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
  });

  // Fetch transaction data
  useEffect(() => {
    const fetchTransactionData = async () => {
      try {
        // Default to today's date if no dates are provided
        const startDate = moment().startOf("day").toISOString();
        const endDate = moment().endOf("day").toISOString();
        const filterObject = { startDate, endDate };
        const { data } = await axios.post(
          `${apiBaseUrl}/api/payments/dateBetween`,
          filterObject
        );
        const sortedData = data.sort((a, b) =>
          moment(b.date).isAfter(moment(a.date)) ? 1 : -1
        );
        setTransactionData(sortedData);
        setFilteredTransactionData(sortedData);
        // Extract unique cashierName, tableNo, and method values
        const uniqueUserIds = Array.from(
          new Set(sortedData.map((record) => record.cashierName))
        ).filter(Boolean);
        setUserIdOptions(uniqueUserIds);
        const uniqueTableNumbers = Array.from(
          new Set(sortedData.map((record) => record.tableNo))
        ).filter(Boolean);
        setTableNumberOptions(uniqueTableNumbers);
        const uniquePaymentMethods = Array.from(
          new Set(sortedData.map((record) => record.method))
        ).filter(Boolean);
        setPaymentMethodOptions(uniquePaymentMethods);
      } catch (error) {
        console.log(error);
        message.error("Failed to fetch transaction data");
      }
    };
    fetchTransactionData();
  }, []);

  // Filtering logic
  useEffect(() => {
    let filtered = [...transactionData];
    if (selectedUserId) {
      filtered = filtered.filter(
        (record) => record.cashierName === selectedUserId
      );
    }
    if (selectedTableNumber) {
      filtered = filtered.filter(
        (record) => String(record.tableNo) === String(selectedTableNumber)
      );
    }
    if (selectedPaymentMethod) {
      filtered = filtered.filter(
        (record) => record.method === selectedPaymentMethod
      );
    }
    if (timeRange && timeRange[0] && timeRange[1]) {
      filtered = filtered.filter((record) => {
        const recordTime = moment(record.date, moment.ISO_8601);
        const start = timeRange[0].clone().set({
          year: recordTime.year(),
          month: recordTime.month(),
          date: recordTime.date(),
        });
        const end = timeRange[1].clone().set({
          year: recordTime.year(),
          month: recordTime.month(),
          date: recordTime.date(),
        });
        return recordTime.isBetween(start, end, undefined, "[]");
      });
    }
    setFilteredTransactionData(filtered);
  }, [
    selectedUserId,
    selectedTableNumber,
    selectedPaymentMethod,
    timeRange,
    transactionData,
  ]);

  const handleSearch = (selectedUserId) => {
    setSearchText(selectedUserId || "");
    if (selectedUserId) {
      setFilteredTransactionData(
        transactionData.filter(
          (record) => record.cashierName === selectedUserId
        )
      );
    } else {
      setFilteredTransactionData(transactionData);
    }
  };

  const formatDateTime = (isoString) => {
    const dateObj = new Date(isoString);
    const date = dateObj.toLocaleDateString().split("T")[0];
    const time = dateObj.toTimeString().split(" ")[0];
    return { date, time };
  };

  // Fetch restaurant settings
  const getSetting = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${apiBaseUrl}/api/settings/get-settings`
      );
      if (data && data.image) {
        setImageLogo(data.image);
        setRestaurantName(data.name);
        setRestaurantAddress(data.address);
      } else {
        setImageLogo(
          "https://bucketergroup.s3.ap-southeast-1.amazonaws.com/posfolder/logo.png"
        );
        setRestaurantName("Klang Bak Kut Teh");
        setRestaurantAddress("xxx");
      }
    } catch (error) {
      message.error("Something Went Wrong");
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  useEffect(() => {
    getSetting();
  }, []);

  const calculateSelectedInvoiceData = (record) => {
    setSelectedInvoice(record);
    setShowInvoiceModal(true);
  };

  const closeInvoiceModal = () => {
    setSelectedInvoice(null);
    setShowInvoiceModal(false);
  };

  // Define table columns (adjust based on your data structure)
  const columns = [
    {
      title: "No",
      dataIndex: "index",
      render: (text, record, recordIndex) => (
        <span>{(pagination.current - 1) * pagination.pageSize + recordIndex + 1}</span>
      ),
    },
    { title: "User ID", dataIndex: "cashierName" },
    {
      title: "Date",
      dataIndex: "date",
      render: (date) => {
        const formattedDate = formatDateTime(date).date;
        return <span>{formattedDate}</span>;
      },
    },
    {
      title: "Time",
      dataIndex: "date",
      render: (date) => {
        const formattedTime = formatDateTime(date).time;
        return <span>{formattedTime}</span>;
      },
    },
    {
      title: "Invoice No.",
      dataIndex: "invoiceId",
      render: (invoiceId, record) => (
        <a
          onClick={() => {
            calculateSelectedInvoiceData(record);
            setShowInvoiceModal(true); // Trigger your modal visibility here
          }}
          style={{
            cursor: "pointer",
            color: "#1890ff", // Ant Design's link blue
            textDecoration: "underline",
          }}
        >
          {invoiceId}
        </a>
      ),
    },
    { title: "Table No.", dataIndex: "tableNo" },
    { title: "Payment Method", dataIndex: "method" },
    // {
    //   title: "Gross Amount (" + symbol + ")",
    //   dataIndex: "billAmount",
    //   render: (_, record) => {
    //     const total = parseFloat(record.billAmount.total || 0);

    //     const serviceSum = (record.billAmount.services || []).reduce(
    //       (acc, service) => acc + parseFloat(service.totalPrice || 0),
    //       0
    //     );

    //     const grossAmount = total + serviceSum;

    //     return <span>{parseFloat(grossAmount).toFixed(2)}</span>;
    //   },
    // },
    // {
    //   title: "Discount (" + symbol + ")",
    //   dataIndex: "billAmount",
    //   render: (_, record) => {
    //     const discount = record.billAmount.discount;
    //     if (discount !== undefined && discount !== null) {
    //       return <span>{parseFloat(discount).toFixed(2)}</span>;
    //     }
    //     return <span>0.00</span>;
    //   },
    // },
    // {
    //   title: "Net Amount (" + symbol + ")",
    //   dataIndex: "billAmount",
    //   render: (_, record) => {
    //     const subTotal = parseFloat(record.billAmount.subTotal || 0);

    //     const serviceSum = (record.billAmount.services || []).reduce(
    //       (acc, service) => acc + parseFloat(service.totalPrice || 0),
    //       0
    //     );

    //     const netAmount = subTotal + serviceSum;

    //     return <span>{parseFloat(netAmount).toFixed(2)}</span>;
    //   },
    // },
    // {
    //   title: "Services (" + symbol + ")",
    //   dataIndex: "billAmount",
    //   render: (_, record) => {
    //     const serviceSum = (record.billAmount.services || []).reduce(
    //       (acc, service) => acc + parseFloat(service.totalPrice || 0),
    //       0
    //     );
    //     return <span>{parseFloat(serviceSum).toFixed(2)}</span>;
    //   },
    // },
    // {
    //   title: "Deposit (" + symbol + ")",
    //   dataIndex: "billAmount",
    //   render: (_, record) => {
    //     const depositAmount = record.billAmount.deposit.amount;
    //     if (depositAmount !== undefined && depositAmount !== null) {
    //       return <span>{parseFloat(depositAmount).toFixed(2)}</span>;
    //     }
    //     return <span>0.00</span>;
    //   },
    // },
    // {
    //   title: "Payable Amount (" + symbol + ")",
    //   dataIndex: "billAmount",
    //   render: (_, record) => {
    //     const payableAmount = record.billAmount.payableAmount;
    //     if (payableAmount !== undefined && payableAmount !== null) {
    //       return <span>{parseFloat(payableAmount).toFixed(2)}</span>;
    //     }
    //     return <span>0.00</span>;
    //   },
    // },
    {
      title: "Revenue (" + symbol + ")",
      dataIndex: "billAmount",
      render: (_, record) => {
        const depositAmount = record.billAmount.deposit.amount;
        const payableAmount = record.billAmount.payableAmount;
        if (
          depositAmount !== undefined &&
          depositAmount !== null &&
          payableAmount !== undefined &&
          payableAmount !== null
        ) {
          const revenue = parseFloat(payableAmount) + parseFloat(depositAmount);
          return <span>{parseFloat(revenue).toFixed(2)}</span>;
        }
        return <span>0.00</span>;
      },
    },
  ];

  // Search/filter controls (rendered above the table)
  const controlStyle = { width: "100%", height: 40 }; // width 100% for responsiveness
  const controlSize = "large";
  const filterControls = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24, alignItems: "center" }}>
      <Col xs={24} sm={12} md={6}>
        <Select
          showSearch
          allowClear
          placeholder="User ID"
          style={controlStyle}
          size={controlSize}
          optionFilterProp="children"
          onChange={setSelectedUserId}
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
          value={selectedUserId || undefined}
        >
          {userIdOptions.map((userId) => (
            <Select.Option key={userId} value={userId}>
              {userId}
            </Select.Option>
          ))}
        </Select>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <TimePicker.RangePicker
          placeholder={["Start Time", "End Time"]}
          format="HH:mm"
          value={timeRange}
          onChange={setTimeRange}
          style={{ ...controlStyle, minWidth: 180 }} // Add minWidth here
          size={controlSize}
          allowClear
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Select
          showSearch
          allowClear
          placeholder="Table Number"
          style={controlStyle}
          size={controlSize}
          optionFilterProp="children"
          onChange={setSelectedTableNumber}
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
          value={selectedTableNumber || undefined}
        >
          {tableNumberOptions.map((tableNo) => (
            <Select.Option key={tableNo} value={tableNo}>
              {tableNo}
            </Select.Option>
          ))}
        </Select>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Select
          showSearch
          allowClear
          placeholder="Payment Method"
          style={controlStyle}
          size={controlSize}
          optionFilterProp="children"
          onChange={setSelectedPaymentMethod}
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
          value={selectedPaymentMethod || undefined}
        >
          {paymentMethodOptions.map((method) => (
            <Select.Option key={method} value={method}>
              {method}
            </Select.Option>
          ))}
        </Select>
      </Col>
    </Row>
  );

  return (
    <DefaultLayout showSearch={false}>
      {filterControls}
      <Table
        columns={columns}
        dataSource={filteredTransactionData}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          pageSizeOptions: ['15', '20', '30', '50', '100'],
          showSizeChanger: filteredTransactionData.length > 15,
          showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total} items`,
          onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
        }}
      />

      <Modal
        title={
          <div>
            Receipt
            <CloseButton
              className="close-button"
              onClick={() => {
                setTimeout(() => {
                  closeInvoiceModal();
                }, 200);
              }}
            />
          </div>
        }
        open={showInvoiceModal}
        onCancel={() => {
          setTimeout(() => {
            closeInvoiceModal();
          }, 200);
        }}
        onOk={async () => {
          setIsPrinting(true); // Disable the button
          try {
            await invoiceRef.current?.printReceipt();
          } catch (error) {
            console.error("Print failed:", error);
          } finally {
            setIsPrinting(false); // Re-enable the button
          }
        }}
        okButtonProps={{ disabled: isPrinting }} // Ensure the button is disabled
        okText="Print"
        className="print-modal"
        maskClosable={false}
      >
        <Invoice orders={selectedInvoice} ref={invoiceRef} />
      </Modal>
    </DefaultLayout>
  );
};

export default StaffTransactionRecordPage;
